// packages/web/src/lib/api.ts — A2.1b: the PWA's one HTTP client. Every
// fetch in this app goes through here, same-origin (packages/server's
// staticWeb plugin serves this app from the SAME origin as /api|/auth —
// see that plugin's own header for why: it's what makes the session
// cookie "just work" with zero CORS wiring). `credentials: 'same-origin'`
// is technically the browser default already for a same-origin fetch, but
// stated explicitly here since it's the one property this whole
// architecture leans on.
//
// Endpoints wired this chunk (mapped against the server's actual routes —
// see packages/server/src/plugins/auth.ts, attention.ts, proposals.ts):
//   POST /auth/magic-link/request  {email}          -> 202 {ok:true}
//   POST /auth/magic-link/verify   {token}          -> {user, isNewUser, recovered}
//   GET  /auth/whoami                               -> {user} | 401
//   POST /auth/logout                                -> {ok:true}
//   GET  /api/attention            (X-Tenant-Id)    -> {attentionItems: [...]}
//   GET  /api/proposals?status=    (X-Tenant-Id)    -> {proposals: [...], limit, offset}
// Approve/reject exist server-side (POST /api/proposals/:id/approve|reject)
// but are NOT called yet — this chunk renders proposals read-only (the
// WRITE loop is the next chunk); Today.tsx wires those buttons to a no-op.
//
// Error shape: two conventions coexist on the server (auth.ts's own header
// notes this) — /auth/* still uses the OLDER flat `{error: string,
// message?}` shape, while every /api/* route uses the newer `{error:
// {code, message}}` shape. ApiError below normalizes both into one shape
// so a caller never has to know which namespace it hit.
export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

interface FlatErrorBody {
  error?: string;
  message?: string;
}
interface NestedErrorBody {
  error?: { code?: string; message?: string };
}

function extractError(status: number, body: unknown): ApiError {
  if (body && typeof body === 'object') {
    const nested = (body as NestedErrorBody).error;
    if (nested && typeof nested === 'object') {
      return new ApiError(status, nested.code ?? 'unknown_error', nested.message ?? 'request failed');
    }
    const flat = body as FlatErrorBody;
    if (typeof flat.error === 'string') {
      return new ApiError(status, flat.error, flat.message ?? flat.error);
    }
  }
  return new ApiError(status, 'unknown_error', 'request failed');
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  tenantId?: string | null;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers = new Headers({ 'Content-Type': 'application/json' });
  if (opts.tenantId) headers.set('X-Tenant-Id', opts.tenantId);

  const resp = await fetch(path, {
    method: opts.method ?? 'GET',
    headers,
    credentials: 'same-origin',
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  const contentType = resp.headers.get('content-type') ?? '';
  const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;

  if (!resp.ok) throw extractError(resp.status, parsed);
  return parsed as T;
}

export interface ApiUser {
  id: string;
  email: string;
  name: string | null;
}

export async function requestMagicLink(email: string): Promise<void> {
  await request('/auth/magic-link/request', { method: 'POST', body: { email } });
}

export async function verifyMagicLink(token: string): Promise<{ user: ApiUser; isNewUser: boolean }> {
  return request('/auth/magic-link/verify', { method: 'POST', body: { token } });
}

/** Never throws on 401 — an absent session is an expected, ordinary
 * outcome for this call (it's how the app tells "logged out" from "logged
 * in"), not an error. Any OTHER failure (network, 5xx) still throws. */
export async function whoami(): Promise<ApiUser | null> {
  try {
    const result = await request<{ user: ApiUser }>('/auth/whoami');
    return result.user;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

export async function logout(): Promise<void> {
  await request('/auth/logout', { method: 'POST' });
}

// ---------------------------------------------------------------------------
// Auth-2: the login + onboarding surfaces. Which methods this environment can
// offer (so Google/Apple only show where credentials exist), email+password
// (so daily users are never bounced to email), password reset, passkeys
// (Face/Touch ID), and tenant resolution/creation that replaces the paste-an-
// id ConnectWorkspace stopgap. Backend: packages/server/src/plugins/auth.ts.
// ---------------------------------------------------------------------------

/** GET /auth/methods — which sign-in methods this environment can actually
 * offer. password + passkey are always true; google/apple are true only when
 * the OAuth credentials exist server-side (in dev with none, they're hidden). */
export interface AuthMethods {
  password: boolean;
  passkey: boolean;
  google: boolean;
  apple: boolean;
}

export async function getAuthMethods(): Promise<AuthMethods> {
  return request<AuthMethods>('/auth/methods');
}

/** POST /auth/password/login — the generic 401 ('Email or password is
 * incorrect.') is surfaced calmly by the caller; never reveals which was wrong. */
export async function passwordLogin(email: string, password: string): Promise<ApiUser> {
  const result = await request<{ user: ApiUser }>('/auth/password/login', {
    method: 'POST',
    body: { email, password },
  });
  return result.user;
}

/** POST /auth/password/register — mints a brand-new account + session and
 * emails a (non-blocking) verification link. `emailVerified` is always false
 * here; the account is usable immediately. A 409 ('account_exists') is
 * surfaced calmly by the caller. */
export async function passwordRegister(
  email: string,
  password: string
): Promise<{ user: ApiUser; emailVerified: boolean }> {
  return request<{ user: ApiUser; emailVerified: boolean }>('/auth/password/register', {
    method: 'POST',
    body: { email, password },
  });
}

/** POST /auth/password/reset-request — always 202 whether or not the email
 * exists (anti-enumeration), so the caller always shows the same calm
 * "check your email" state. */
export async function passwordResetRequest(email: string): Promise<void> {
  await request('/auth/password/reset-request', { method: 'POST', body: { email } });
}

/** One tenant membership the signed-in caller belongs to (GET /auth/tenants). */
export interface TenantMembership {
  tenantId: string;
  name: string;
  slug: string | null;
  vertical: string;
  role: string;
}

export async function listTenants(): Promise<TenantMembership[]> {
  const result = await request<{ tenants: TenantMembership[] }>('/auth/tenants');
  return result.tenants;
}

/** POST /auth/tenants — create a workspace. `vertical` is set for the caller
 * (the actor never sees the word); returns the new tenantId to store + send as
 * X-Tenant-Id thereafter. */
export async function createTenant(
  name: string,
  vertical: string,
  slug?: string
): Promise<{ tenantId: string; membershipId: string }> {
  return request<{ tenantId: string; membershipId: string }>('/auth/tenants', {
    method: 'POST',
    body: slug ? { name, vertical, slug } : { name, vertical },
  });
}

/** PUT /api/actor/profile — upsert the singleton profile (name + union here). */
export async function updateActorProfile(
  tenantId: string,
  fields: Record<string, unknown>
): Promise<{ id: string; fields: Record<string, unknown> }> {
  const result = await request<{ profile: { id: string; fields: Record<string, unknown> } }>(
    '/api/actor/profile',
    { method: 'PUT', tenantId, body: { fields } }
  );
  return result.profile;
}

// ---- Passkeys (Face/Touch ID). The server hands back WebAuthn ceremony
// options; the browser (@simplewebauthn/browser) drives the authenticator and
// posts the response back for verification. `options` is passed through
// verbatim to startAuthentication/startRegistration. ---------------------------

/** POST /auth/passkey/login/options — begin an authentication ceremony. */
export async function passkeyLoginOptions(
  email: string
): Promise<{ ceremonyId: string; options: unknown }> {
  return request<{ ceremonyId: string; options: unknown }>('/auth/passkey/login/options', {
    method: 'POST',
    body: { email },
  });
}

/** POST /auth/passkey/login/verify — finish authentication; sets the session. */
export async function passkeyLoginVerify(ceremonyId: string, response: unknown): Promise<ApiUser> {
  const result = await request<{ user: ApiUser }>('/auth/passkey/login/verify', {
    method: 'POST',
    body: { ceremonyId, response },
  });
  return result.user;
}

/** POST /auth/passkey/register/options — begin a registration ceremony (the
 * caller must already be signed in). */
export async function passkeyRegisterOptions(): Promise<{ ceremonyId: string; options: unknown }> {
  return request<{ ceremonyId: string; options: unknown }>('/auth/passkey/register/options', {
    method: 'POST',
    body: {},
  });
}

/** POST /auth/passkey/register/verify — finish registration. */
export async function passkeyRegisterVerify(ceremonyId: string, response: unknown): Promise<void> {
  await request('/auth/passkey/register/verify', { method: 'POST', body: { ceremonyId, response } });
}

export type AttentionSeverity = 'info' | 'warning' | 'critical';

export interface AttentionItem {
  id: string;
  userId: string | null;
  itemType: string;
  objectType: string | null;
  objectId: string | null;
  severity: AttentionSeverity;
  reason: string | null;
  sourceRef: Record<string, unknown> | null;
  dedupKey: string;
  createdAt: string;
  resolvedAt: string | null;
  lastRaisedAt: string;
}

export async function listAttentionItems(tenantId: string): Promise<AttentionItem[]> {
  const result = await request<{ attentionItems: AttentionItem[] }>('/api/attention', { tenantId });
  return result.attentionItems;
}

export type ProposalStatus = 'pending' | 'approved' | 'rejected' | 'expired';

export interface Proposal {
  id: string;
  kind: string;
  targetType: string;
  targetId: string | null;
  proposed: Record<string, unknown>;
  rationale: string | null;
  sourceRef: Record<string, unknown> | null;
  confidence: number | null;
  status: ProposalStatus;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionReason: string | null;
  createdAt: string;
}

export async function listProposals(tenantId: string, status: ProposalStatus = 'pending'): Promise<Proposal[]> {
  const result = await request<{ proposals: Proposal[] }>(`/api/proposals?status=${status}`, { tenantId });
  return result.proposals;
}

/** POST /api/proposals/:id/approve — the propose->approve primitive resolved
 * INLINE from a Today card. Materializes the proposal's commitment and returns
 * its identity (packages/server/src/plugins/proposals.ts), so the card can
 * refresh straight to the settled state without a second round-trip. */
export interface ApproveProposalResult {
  proposalId: string;
  commitment: { targetType: string; id: string | null };
}

export async function approveProposal(tenantId: string, id: string): Promise<ApproveProposalResult> {
  return request<ApproveProposalResult>(`/api/proposals/${id}/approve`, { method: 'POST', tenantId });
}

/** POST /api/proposals/:id/reject — the "Not now" side of the same inline
 * decision. reason is optional (the server's rejectBodySchema accepts an empty
 * body). */
export async function rejectProposal(
  tenantId: string,
  id: string,
  reason?: string
): Promise<{ proposalId: string }> {
  return request<{ proposalId: string }>(`/api/proposals/${id}/reject`, {
    method: 'POST',
    tenantId,
    body: reason ? { reason } : {},
  });
}

// ---------------------------------------------------------------------------
// R5b (WS4a): the KNOT CAPTURE "second door" — push the Knot -> capture ->
// propose -> approve. captureKnot posts a free-text note and/or uploaded
// originals (pdf/image/audio) to the R5a backend (plugins/actorCapture.ts),
// which RETAINS every file as an actor_source at capture time and stages a
// deterministic, context-driven proposal in the SAME review queue Today reads.
// The returned proposalId is then resolved through the existing
// approveProposal / rejectProposal above — one approval path, no parallel loop.
// ---------------------------------------------------------------------------

/** The page the Knot was pressed on. `route` is the current hash path;
 * `subjectType`/`subjectId` pre-associate the capture with the record being
 * viewed (project detail -> actor_project, audition detail -> actor_audition)
 * so a file dropped there attaches to it. All fields optional — a capture from
 * a list screen carries just the route, or nothing the backend can act on
 * (it falls back to the inbox). */
export interface CaptureContext {
  route?: string;
  subjectType?: string;
  subjectId?: string;
}

/** The preview the backend hands back for the staged proposal (201). `summary`
 * is the human line; `captureKind` is attach_to_subject | note_on_subject |
 * generic_inbox; `sourceIds` are the actor_source ids already retained. */
export interface CapturePreview {
  summary: string;
  captureKind: string;
  sourceIds: string[];
  actionCount: number;
}

export interface CaptureResult {
  proposalId: string;
  preview: CapturePreview;
}

/** POST /api/actor/capture (multipart) — hand the Knot a note and/or files.
 * Bare fetch so the browser sets the multipart boundary itself (same pattern as
 * uploadMedia/uploadCallSheet). `context` rides as a single JSON `context`
 * field the backend also accepts. Throws ApiError on any non-2xx (e.g. the 400
 * when neither text nor a file is present — callers guard before calling). */
export async function captureKnot(
  tenantId: string,
  text: string,
  files: File[],
  context: CaptureContext = {}
): Promise<CaptureResult> {
  const form = new FormData();
  if (text.trim()) form.set('text', text);
  for (const file of files) form.append('files', file, file.name);
  if (context.route || context.subjectType || context.subjectId) {
    form.set('context', JSON.stringify(context));
  }
  const headers = new Headers();
  if (tenantId) headers.set('X-Tenant-Id', tenantId);
  const resp = await fetch('/api/actor/capture', {
    method: 'POST',
    headers,
    credentials: 'same-origin',
    body: form,
  });
  const contentType = resp.headers.get('content-type') ?? '';
  const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
  if (!resp.ok) throw extractError(resp.status, parsed);
  return parsed as CaptureResult;
}

// B2: the generic Records API (packages/server/src/plugins/records.ts) —
// the raw shape every /records* route hands back verbatim (snake_case,
// straight off the RecordRow the core records engine returns; unlike
// attention/proposals above, this route never re-cases into camelCase).
export interface RecordRow {
  id: string;
  tenant_id: string;
  record_type: string;
  display_name: string;
  fields: Record<string, unknown>;
  lifecycle_state: string | null;
  sensitivity: 'normal' | 'restricted';
  status: 'active' | 'archived' | 'deleted';
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/** GET /records?type_key=&lifecycle_state= — reused as-is for the
 * Auditions list/stage-filter (no dedicated actor route needed: the
 * generic list already supports exactly this filter shape). Rows come
 * back oldest-first (records.ts's own ORDER BY); callers wanting
 * reverse-chron reverse the array themselves rather than this function
 * silently reordering a generic primitive's contract. */
export async function listRecordsByType(
  tenantId: string,
  typeKey: string,
  lifecycleState?: string
): Promise<RecordRow[]> {
  const qs = new URLSearchParams({ type_key: typeKey });
  if (lifecycleState) qs.set('lifecycle_state', lifecycleState);
  const result = await request<{ records: RecordRow[] }>(`/records?${qs.toString()}`, { tenantId });
  return result.records;
}

/** POST /records/:id/transition {to} — reused as-is for stage advance/
 * archive/revive (the lifecycle engine already rejects illegal transitions
 * with a 422 lifecycle_guard ApiError; no dedicated actor route needed). */
export async function transitionRecord(tenantId: string, id: string, to: string): Promise<RecordRow> {
  const result = await request<{ record: RecordRow }>(`/records/${id}/transition`, {
    method: 'POST',
    tenantId,
    body: { to },
  });
  return result.record;
}

// R2: the SCHEMA-DRIVEN record-operations engine. The generic RecordForm /
// RecordActions render entirely from a record type's OWN definition (fields,
// picklist values, relationships, lifecycle) fetched from these endpoints —
// so R3 can roll manual CRUD out to every record type from one system.

export type SchemaFieldType =
  | 'text'
  | 'number'
  | 'currency'
  | 'date'
  | 'datetime'
  | 'picklist'
  | 'boolean'
  | 'duration'
  | 'file';

export interface SchemaField {
  name: string;
  type: SchemaFieldType;
  required: boolean;
  label: string;
  picklistValues?: string[];
  default?: unknown;
}

export interface SchemaRelationship {
  key: string;
  targetType: string;
  kind: 'lookup' | 'master_detail';
  cardinality: 'one' | 'many';
  label: string;
}

export interface SchemaLifecycle {
  states: string[];
  /** from-state -> the legal next states (the engine's exact {from,to} pairs). */
  transitions: Record<string, string[]>;
}

export interface RecordTypeSchema {
  key: string;
  label: string;
  labelPlural: string;
  nameTemplate: string;
  fields: SchemaField[];
  relationships: SchemaRelationship[];
  lifecycle?: SchemaLifecycle;
}

export interface RecordTypeSummary {
  key: string;
  label: string;
  labelPlural: string;
  hasLifecycle: boolean;
}

/** GET /records/types — the tenant pack's record types (summary list). */
export async function getRecordTypes(tenantId: string): Promise<RecordTypeSummary[]> {
  const result = await request<{ types: RecordTypeSummary[] }>('/records/types', { tenantId });
  return result.types;
}

/** GET /records/types/:key — one record type's full schema. */
export async function getRecordTypeSchema(tenantId: string, key: string): Promise<RecordTypeSchema> {
  const result = await request<{ schema: RecordTypeSchema }>(
    `/records/types/${encodeURIComponent(key)}`,
    { tenantId }
  );
  return result.schema;
}

/** One relationship link row (GET /records/:id?include=links). */
export interface LinkRow {
  id: string;
  src_type: string;
  src_id: string;
  dst_type: string;
  dst_id: string;
  link_type: string;
  label: string | null;
}

/** GET /records/:id?include=links — the record + its links (edit-mode
 * prefill: relationship pickers seed from these). */
export async function getRecordWithLinks(
  tenantId: string,
  id: string
): Promise<{ record: RecordRow; links: LinkRow[] }> {
  const result = await request<{ record: RecordRow; links?: LinkRow[] }>(
    `/records/${id}?include=links`,
    { tenantId }
  );
  return { record: result.record, links: result.links ?? [] };
}

/** POST /records — create a record of `recordType` with `fields`. */
export async function createRecord(
  tenantId: string,
  recordType: string,
  fields: Record<string, unknown>,
  lifecycleState?: string | null
): Promise<RecordRow> {
  const body: Record<string, unknown> = { recordType, fields };
  if (lifecycleState !== undefined) body['lifecycleState'] = lifecycleState;
  const result = await request<{ record: RecordRow }>('/records', {
    method: 'POST',
    tenantId,
    body,
  });
  return result.record;
}

/** PATCH /records/:id — edit a record's fields. */
export async function updateRecord(
  tenantId: string,
  id: string,
  fields: Record<string, unknown>
): Promise<RecordRow> {
  const result = await request<{ record: RecordRow }>(`/records/${id}`, {
    method: 'PATCH',
    tenantId,
    body: { fields },
  });
  return result.record;
}

/** POST /records/:id/delete — soft-delete (spec §5 rule 3: nothing is lost). */
export async function deleteRecord(tenantId: string, id: string): Promise<RecordRow> {
  const result = await request<{ record: RecordRow }>(`/records/${id}/delete`, {
    method: 'POST',
    tenantId,
  });
  return result.record;
}

/** POST /records/:id/links — link `id` to `dstId` via `relationshipKey`
 * (the relationship is declared on the SOURCE type, so srcId = id). */
export async function linkRecord(
  tenantId: string,
  id: string,
  relationshipKey: string,
  dstId: string
): Promise<void> {
  await request(`/records/${id}/links`, {
    method: 'POST',
    tenantId,
    body: { relationshipKey, dstId },
  });
}

/** DELETE /records/:id/links/:linkId — remove one link (lookup relationships;
 * master_detail unlinks are refused 409 by the engine). */
export async function unlinkRecord(tenantId: string, id: string, linkId: string): Promise<void> {
  await request(`/records/${id}/links/${linkId}`, { method: 'DELETE', tenantId });
}

/** POST /records/blobs — upload a file through the media pipeline, returning
 * the ref string a `file` record field stores. Bare fetch so the browser sets
 * the multipart boundary itself (same as uploadMedia/uploadCallSheet). */
export async function uploadRecordBlob(tenantId: string, file: File): Promise<{ ref: string; mimeType: string }> {
  const form = new FormData();
  form.set('file', file, file.name);
  const headers = new Headers();
  if (tenantId) headers.set('X-Tenant-Id', tenantId);
  const resp = await fetch('/records/blobs', {
    method: 'POST',
    headers,
    credentials: 'same-origin',
    body: form,
  });
  const contentType = resp.headers.get('content-type') ?? '';
  const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
  if (!resp.ok) throw extractError(resp.status, parsed);
  return parsed as { ref: string; mimeType: string };
}

// B2: the Actor vertical's two thin routes (packages/server/src/plugins/
// actorAuditions.ts) — see that file's own header for why the generic
// Records API above isn't enough for these two specifically.
// B3a: the compact source summary the composed audition detail now carries
// inline (kind + filename + id), so the detail screen can offer a "view the
// original breakdown" chip that deep-links to GET /api/actor/sources/:id.
export interface AuditionSourceRef {
  id: string;
  kind: string | null;
  filename: string | null;
}

export interface AuditionDetail {
  audition: RecordRow;
  sessions: RecordRow[];
  castingDirector: RecordRow | null;
  project: RecordRow | null;
  // B3a: present when this audition was derived from an actor_source (its
  // 'source' lookup). Older responses may omit it — treated as null.
  source?: AuditionSourceRef | null;
  // B6c-2: the audition's sides revisions (same shape the project detail
  // surfaces). Older responses may omit it — callers default to [].
  sides?: ProjectSides[];
}

export async function getAuditionDetail(tenantId: string, id: string): Promise<AuditionDetail> {
  return request<AuditionDetail>(`/api/actor/auditions/${id}`, { tenantId });
}

// B3: the Projects surface. The list reuses the generic listRecordsByType
// above (type_key=actor_project, lifecycle_state=<phase>); only these three
// composed/enrichment reads need the vertical routes (plugins/actorProjects.ts).

/** GET /api/actor/projects/:id — the composed phase-adaptive detail: the
 * project record itself plus its auditions, the people across those
 * auditions, and the source documents behind the project + its auditions. */
/** B6b: a shoot-day record plus the id of the ORIGINAL source (call sheet)
 * it was parsed from, so the day-of card's "view source" chip can open that
 * exact original. `sourceId` is null for a shoot day with no linked source. */
export interface ProjectShootDay extends RecordRow {
  sourceId: string | null;
}

/** B6c-2: one sides revision plus the id of its retained ORIGINAL file
 * (`sourceId` → SourceViewer) and the id of the prior revision it replaced
 * (`supersedesId`, null for a first revision). is_current (in `fields`) marks
 * the live one; a current revision WITH a supersedesId is what raises the calm
 * "new revision" change flag. Same attach-the-source shape as ProjectShootDay. */
export interface ProjectSides extends RecordRow {
  sourceId: string | null;
  supersedesId: string | null;
}

/** B6c-2: one contract plus the id of its retained ORIGINAL file. The guarded
 * lifecycle lives in `lifecycle_state` (received/under_review/signed/
 * countersigned/declined); `fields` carries kind/rate/respond_by/etc. */
export interface ProjectContract extends RecordRow {
  sourceId: string | null;
}

export interface ProjectDetail {
  project: RecordRow;
  auditions: RecordRow[];
  people: RecordRow[];
  sources: RecordRow[];
  // B6b additive fields — older responses may omit them; callers default to [].
  shootDays?: ProjectShootDay[];
  workedWith?: RecordRow[];
  // B6c-2 additive fields — the real sides revisions + contracts for this
  // project (older responses may omit them; callers default to []).
  sides?: ProjectSides[];
  contracts?: ProjectContract[];
  // WS5 additive field — the current uploaded poster (newest kind='poster'
  // media linked to this project), or null to fall back to the gradient art.
  poster?: { id: string; fileUrl: string } | null;
}

export async function getProjectDetail(tenantId: string, id: string): Promise<ProjectDetail> {
  return request<ProjectDetail>(`/api/actor/projects/${id}`, { tenantId });
}

// The medium hints the enrichment search actually honours (plugins/
// actorProjects.ts coerces anything else to "no hint"); the actor_project
// `medium` picklist is wider (film/tv/theatre/new_media/commercial/other),
// so callers map their own value down to one of these or omit it.
export type EnrichMedium = 'film' | 'tv' | 'voice';

/** One ranked Wikidata candidate for the confirm-a-match UI — the server
 * strips the internal `_e` entity plumbing before it reaches here (see
 * actorProjects.ts's enrich/search route). Ordered highest-confidence first
 * (the module's own score sort); `score` is the raw rank hint, never a
 * percentage. */
export interface EnrichCandidate {
  qid: string;
  label: string | undefined;
  description: string | undefined;
  type: string | undefined;
  year: string;
  score: number;
}

export interface EnrichSearchParams {
  title: string;
  medium?: EnrichMedium;
  year?: string;
}

/** GET /api/actor/projects/:id/enrich/search — ranked candidates. Reads
 * only; never writes. NEVER auto-applies (the actor confirms one, then
 * applyProjectEnrichment writes it). */
export async function searchProjectEnrichment(
  tenantId: string,
  id: string,
  params: EnrichSearchParams
): Promise<EnrichCandidate[]> {
  const qs = new URLSearchParams({ title: params.title });
  if (params.medium) qs.set('medium', params.medium);
  if (params.year) qs.set('year', params.year);
  const result = await request<{ candidates: EnrichCandidate[] }>(
    `/api/actor/projects/${id}/enrich/search?${qs.toString()}`,
    { tenantId }
  );
  return result.candidates;
}

/** The enrichment the actor confirmed, as written back onto the project. */
export interface AppliedEnrichment {
  qid: string;
  wikidataUrl: string;
  label: string | undefined;
  description: string | null;
  type: string | undefined;
  airDate: string | null;
  genres: string[];
  imdbId: string | null;
  tmdbId: string | null;
}

/** POST /api/actor/projects/:id/enrich/apply { qid } — writes the confirmed
 * enrichment fields onto the project and stamps enrichment_confirmed_at. The
 * 400-without-qid guard lives server-side; callers here always pass one. */
export async function applyProjectEnrichment(
  tenantId: string,
  id: string,
  qid: string
): Promise<{ project: RecordRow; enrichment: AppliedEnrichment }> {
  return request<{ project: RecordRow; enrichment: AppliedEnrichment }>(
    `/api/actor/projects/${id}/enrich/apply`,
    { method: 'POST', tenantId, body: { qid } }
  );
}

// B3a provenance: the source-document viewer read model. An actor_source is
// the ORIGINAL, untouched document (breakdown/call sheet/contract/sides/
// email) a derived record was built from.
export interface SourceDoc {
  id: string;
  kind: string | null;
  filename: string | null;
  raw_text: string | null;
  media_ref: string | null;
  received_at: string | null;
}

export async function getSource(tenantId: string, id: string): Promise<SourceDoc> {
  return request<SourceDoc>(`/api/actor/sources/${id}`, { tenantId });
}

/** B6b (Huan 2026-07-13 correction): the URL of the ORIGINAL saved file —
 * GET /api/actor/sources/:id/original streams the retained bytes (a call-sheet
 * PDF/image) in their original form. `tenantId` is part of the signature
 * because the endpoint is tenant-scoped, but scoping travels in the X-Tenant-Id
 * HEADER (see fetchSourceOriginal) — a bare <iframe>/<img> src can't set that
 * header, so embedders fetch the bytes and use an object URL instead. */
export function sourceOriginalUrl(_tenantId: string, id: string): string {
  return `/api/actor/sources/${id}/original`;
}

/** The retained original as an in-memory object URL + its mime type. Fetched
 * with the tenant header (which an <iframe>/<img> src can't carry), so the
 * result is embeddable directly. Callers MUST URL.revokeObjectURL(objectUrl)
 * when done. Throws ApiError on any non-2xx (e.g. 404 for a source with no
 * stored original). */
export interface SourceOriginal {
  objectUrl: string;
  mimeType: string;
  /** true when the server served the file inline (a render-safe type); false
   * when it forced a download (content-disposition: attachment). */
  inline: boolean;
}

export async function fetchSourceOriginal(tenantId: string, id: string): Promise<SourceOriginal> {
  const headers = new Headers();
  if (tenantId) headers.set('X-Tenant-Id', tenantId);
  const resp = await fetch(sourceOriginalUrl(tenantId, id), { headers, credentials: 'same-origin' });
  if (!resp.ok) {
    const contentType = resp.headers.get('content-type') ?? '';
    const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
    throw extractError(resp.status, parsed);
  }
  const mimeType = (resp.headers.get('content-type') ?? 'application/octet-stream').split(';')[0]!.trim();
  const inline = (resp.headers.get('content-disposition') ?? '').toLowerCase().includes('inline');
  const blob = await resp.blob();
  return { objectUrl: URL.createObjectURL(blob), mimeType, inline };
}

/** B6b: the composed detail shape POST /api/actor/call-sheets returns. */
export interface CallSheetIngestResult {
  sourceId: string;
  shootDayId: string;
  peopleLinked: number;
}

/** POST /api/actor/call-sheets — uploads an ORIGINAL call-sheet file
 * (multipart) for a booked project; the server retains the original, parses a
 * shoot day, and links worked-with people. Uses a bare fetch (not `request`)
 * so the browser sets the multipart boundary itself. */
export async function uploadCallSheet(
  tenantId: string,
  projectId: string,
  file: File
): Promise<CallSheetIngestResult> {
  const form = new FormData();
  form.set('project_id', projectId);
  form.set('file', file, file.name);
  const headers = new Headers();
  if (tenantId) headers.set('X-Tenant-Id', tenantId);
  const resp = await fetch('/api/actor/call-sheets', {
    method: 'POST',
    headers,
    credentials: 'same-origin',
    body: form,
  });
  const contentType = resp.headers.get('content-type') ?? '';
  const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
  if (!resp.ok) throw extractError(resp.status, parsed);
  return parsed as CallSheetIngestResult;
}

// B6c-2: sides + contracts uploads (multipart, mirroring uploadCallSheet
// above) and the guarded contract status transition.

/** POST /api/actor/sides response. `changeFlag` is true when this revision
 * SUPERSEDED a prior one (the calm "new revision" signal). */
export interface SidesIngestResult {
  sidesId: string;
  sourceId: string;
  supersededId: string | null;
  changeFlag: boolean;
}

/** Optional metadata to stamp on the new sides revision. */
export interface SidesUploadMeta {
  revisionColour?: string;
  versionLabel?: string;
  pagesOrScenesAffected?: string;
  changeSummary?: string;
}

/** POST /api/actor/sides — uploads an ORIGINAL sides file (multipart) anchored
 * to a project and/or audition. The server retains the original, creates the
 * new revision, and supersedes the prior current one. Bare fetch so the
 * browser sets the multipart boundary itself (same as uploadCallSheet). */
export async function uploadSides(
  tenantId: string,
  anchor: { projectId?: string; auditionId?: string },
  file: File,
  meta: SidesUploadMeta = {}
): Promise<SidesIngestResult> {
  const form = new FormData();
  if (anchor.projectId) form.set('project_id', anchor.projectId);
  if (anchor.auditionId) form.set('audition_id', anchor.auditionId);
  if (meta.revisionColour) form.set('revision_colour', meta.revisionColour);
  if (meta.versionLabel) form.set('version_label', meta.versionLabel);
  if (meta.pagesOrScenesAffected) form.set('pages_or_scenes_affected', meta.pagesOrScenesAffected);
  if (meta.changeSummary) form.set('change_summary', meta.changeSummary);
  form.set('file', file, file.name);
  const headers = new Headers();
  if (tenantId) headers.set('X-Tenant-Id', tenantId);
  const resp = await fetch('/api/actor/sides', {
    method: 'POST',
    headers,
    credentials: 'same-origin',
    body: form,
  });
  const contentType = resp.headers.get('content-type') ?? '';
  const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
  if (!resp.ok) throw extractError(resp.status, parsed);
  return parsed as SidesIngestResult;
}

/** POST /api/actor/contracts response. `commitmentId` is set when a
 * `respond_by` created a response Commitment. */
export interface ContractIngestResult {
  contractId: string;
  sourceId: string;
  commitmentId: string | null;
}

export interface ContractUploadMeta {
  kind?: string;
  rate?: string;
  keyDates?: string;
  respondBy?: string;
  termsNote?: string;
}

/** POST /api/actor/contracts — uploads an ORIGINAL contract file (multipart)
 * for a project. The server retains the original + (respond_by) spins up a
 * real response Commitment. */
export async function uploadContract(
  tenantId: string,
  projectId: string,
  file: File,
  meta: ContractUploadMeta = {}
): Promise<ContractIngestResult> {
  const form = new FormData();
  form.set('project_id', projectId);
  if (meta.kind) form.set('kind', meta.kind);
  if (meta.rate) form.set('rate', meta.rate);
  if (meta.keyDates) form.set('key_dates', meta.keyDates);
  if (meta.respondBy) form.set('respond_by', meta.respondBy);
  if (meta.termsNote) form.set('terms_note', meta.termsNote);
  form.set('file', file, file.name);
  const headers = new Headers();
  if (tenantId) headers.set('X-Tenant-Id', tenantId);
  const resp = await fetch('/api/actor/contracts', {
    method: 'POST',
    headers,
    credentials: 'same-origin',
    body: form,
  });
  const contentType = resp.headers.get('content-type') ?? '';
  const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
  if (!resp.ok) throw extractError(resp.status, parsed);
  return parsed as ContractIngestResult;
}

/** POST /api/actor/contracts/:id/status { to } — the GUARDED lifecycle
 * transition. An illegal transition is a 422 ApiError (surfaced calmly by the
 * caller). `commitmentClosed` is true when reaching signed/countersigned
 * closed the linked response Commitment. */
export async function setContractStatus(
  tenantId: string,
  id: string,
  to: string
): Promise<{ contract: RecordRow; commitmentClosed: boolean }> {
  return request<{ contract: RecordRow; commitmentClosed: boolean }>(`/api/actor/contracts/${id}/status`, {
    method: 'POST',
    tenantId,
    body: { to },
  });
}

export type SessionModality = 'in_person' | 'zoom' | 'self_tape';

export interface CreateSessionInput {
  sessionKind: string;
  round?: number;
  modality?: SessionModality;
  scheduledAt?: string;
  locationOrLink?: string;
  prepNotes?: string;
  travelNeeded?: boolean;
}

export interface CreateSessionResult {
  session: RecordRow;
  scheduling: { kind: 'commitment'; commitmentId: string } | null;
}

export async function createAuditionSession(
  tenantId: string,
  auditionId: string,
  input: CreateSessionInput
): Promise<CreateSessionResult> {
  return request<CreateSessionResult>(`/api/actor/auditions/${auditionId}/sessions`, {
    method: 'POST',
    tenantId,
    body: input,
  });
}

// ---------------------------------------------------------------------------
// B5b: MATERIALS — media (headshots/reels/clips), resume assembly, and the
// training/award/skill CRUD behind the resume. Backend: actorMedia.ts +
// actorResume.ts. Media BYTES are fetched with the tenant header and handed
// back as an object URL (fetchMediaFile) — a bare <img>/<video> src can't carry
// X-Tenant-Id, exactly the constraint fetchSourceOriginal solves above.
// ---------------------------------------------------------------------------

// WS5: 'poster' (project art) and 'avatar' (person portrait) added — both
// user-supplied because Wikidata gives metadata but never such imagery.
export type MediaKind = 'headshot' | 'reel' | 'clip' | 'other' | 'poster' | 'avatar';

/** One media row as GET /api/actor/media hands it back: the raw record fields
 * (kind/label/category/is_selected/on_resume/is_current/version_label/duration/
 * notes) plus its project + person links and the /file URL for its stored
 * bytes. */
export interface ActorMedia {
  id: string;
  fields: Record<string, unknown>;
  projectId: string | null;
  // WS5: the person this media is of (an avatar's subject); null otherwise.
  personId: string | null;
  fileUrl: string;
}

/** GET /api/actor/media?kind=&person=&project= — newest first. `person`/
 * `project` narrow to media linked to that record (e.g. kind='avatar' +
 * person yields that person's avatars, newest = index 0). */
export async function listMedia(
  tenantId: string,
  kind?: MediaKind,
  opts: { personId?: string; projectId?: string } = {}
): Promise<ActorMedia[]> {
  const qs = new URLSearchParams();
  if (kind) qs.set('kind', kind);
  if (opts.personId) qs.set('person', opts.personId);
  if (opts.projectId) qs.set('project', opts.projectId);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const result = await request<{ media: ActorMedia[] }>(`/api/actor/media${suffix}`, { tenantId });
  return result.media;
}

/** The stored image/video as an in-memory object URL + mime type. Fetched with
 * the tenant header (which an <img>/<video> src can't carry). Callers MUST
 * URL.revokeObjectURL(objectUrl) when done (see components/MediaEl.tsx, which
 * does this on unmount/refetch). Throws ApiError on any non-2xx (404 for media
 * with no stored file). */
export interface MediaFile {
  objectUrl: string;
  mimeType: string;
}

export function mediaFileUrl(id: string): string {
  return `/api/actor/media/${id}/file`;
}

export async function fetchMediaFile(tenantId: string, id: string): Promise<MediaFile> {
  const headers = new Headers();
  if (tenantId) headers.set('X-Tenant-Id', tenantId);
  const resp = await fetch(mediaFileUrl(id), { headers, credentials: 'same-origin' });
  if (!resp.ok) {
    const contentType = resp.headers.get('content-type') ?? '';
    const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
    throw extractError(resp.status, parsed);
  }
  const mimeType = (resp.headers.get('content-type') ?? 'application/octet-stream').split(';')[0]!.trim();
  const blob = await resp.blob();
  return { objectUrl: URL.createObjectURL(blob), mimeType };
}

export interface MediaUploadMeta {
  label?: string;
  category?: string;
  versionLabel?: string;
  duration?: string;
  notes?: string;
  projectId?: string;
  // WS5: the person an avatar is of (kind='avatar').
  personId?: string;
}

/** POST /api/actor/media — uploads the ORIGINAL image/video (multipart) and
 * creates the actor_media row. Bare fetch so the browser sets the multipart
 * boundary itself (same as uploadCallSheet/uploadSides). */
export async function uploadMedia(
  tenantId: string,
  kind: MediaKind,
  file: File,
  meta: MediaUploadMeta = {}
): Promise<{ mediaId: string }> {
  const form = new FormData();
  form.set('kind', kind);
  if (meta.label) form.set('label', meta.label);
  if (meta.category) form.set('category', meta.category);
  if (meta.versionLabel) form.set('version_label', meta.versionLabel);
  if (meta.duration) form.set('duration', meta.duration);
  if (meta.notes) form.set('notes', meta.notes);
  if (meta.projectId) form.set('project_id', meta.projectId);
  if (meta.personId) form.set('person_id', meta.personId);
  form.set('file', file, file.name);
  const headers = new Headers();
  if (tenantId) headers.set('X-Tenant-Id', tenantId);
  const resp = await fetch('/api/actor/media', {
    method: 'POST',
    headers,
    credentials: 'same-origin',
    body: form,
  });
  const contentType = resp.headers.get('content-type') ?? '';
  const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
  if (!resp.ok) throw extractError(resp.status, parsed);
  return parsed as { mediaId: string };
}

/** POST /api/actor/media/:id/select — toggle curation flags. is_selected is
 * MULTI; on_resume + is_current are SINGULAR (setting one unsets the prior of
 * the same kind; is_current also links this reel's supersedes at the demoted
 * cut). `supersededId` is the demoted reel's id (null for the first current). */
export interface MediaSelectFlags {
  is_selected?: boolean;
  on_resume?: boolean;
  is_current?: boolean;
}

export interface MediaSelectResult {
  media: RecordRow;
  is_selected: boolean;
  on_resume: boolean;
  is_current: boolean;
  supersededId: string | null;
}

export async function selectMedia(
  tenantId: string,
  id: string,
  flags: MediaSelectFlags
): Promise<MediaSelectResult> {
  return request<MediaSelectResult>(`/api/actor/media/${id}/select`, {
    method: 'POST',
    tenantId,
    body: flags,
  });
}

// ---- Resume assembly + the training/award/skill inputs behind it. ----

export type ResumeVariant = 'full' | 'theatrical' | 'commercial' | 'voice';

export interface ResumeHeadshot {
  mediaId: string;
  fileUrl: string;
  label: string | null;
}

export interface ResumeCredit {
  projectId: string;
  production: string | null;
  role: string | null;
  medium: string | null;
  company: string | null;
}

export interface ResumeCreditGroup {
  group: string;
  credits: ResumeCredit[];
}

/** A training / award / skill row (the resume's editable sections) — the raw
 * record id + fields, exactly as the resume/CRUD routes return them. */
export interface ResumeEntry {
  id: string;
  fields: Record<string, unknown>;
}

export interface Resume {
  variant: string;
  headshot: ResumeHeadshot | null;
  creditGroups: ResumeCreditGroup[];
  training: ResumeEntry[];
  awards: ResumeEntry[];
  skills: ResumeEntry[];
}

export async function getResume(tenantId: string, variant?: ResumeVariant): Promise<Resume> {
  const qs = variant ? `?variant=${encodeURIComponent(variant)}` : '';
  return request<Resume>(`/api/actor/resume${qs}`, { tenantId });
}

/** The three resume sections that have their own CRUD endpoints. */
export type ResumeSectionKind = 'training' | 'awards' | 'skills';

export async function createResumeEntry(
  tenantId: string,
  kind: ResumeSectionKind,
  fields: Record<string, unknown>
): Promise<ResumeEntry> {
  const result = await request<{ record: RecordRow }>(`/api/actor/${kind}`, {
    method: 'POST',
    tenantId,
    body: { fields },
  });
  return { id: result.record.id, fields: result.record.fields };
}

export async function deleteResumeEntry(
  tenantId: string,
  kind: ResumeSectionKind,
  id: string
): Promise<void> {
  await request(`/api/actor/${kind}/${id}`, { method: 'DELETE', tenantId });
}

// ---------------------------------------------------------------------------
// B4b: CAREER — the Money / Documents / Stats read models + the expense ledger
// CRUD. Backends: actorMoney.ts, actorDocuments.ts, actorStats.ts. Every rate
// the Stats surface hands back (callbackRate/bookingRate/...) is a FRACTION in
// [0,1], never a percentage — the surface multiplies by 100 to render. Money
// numbers are raw currency values (no unit baked in); the surface owns the $.
// ---------------------------------------------------------------------------

/** One income-by-type tile: gross/net sums, the effective commission % across
 * the type, its count, and the overdue slice that colours the tile's severity. */
export interface MoneyIncomeType {
  type: string;
  gross: number;
  net: number;
  commissionPct: number;
  count: number;
  overdueGross: number;
}

export interface MoneyExpenseCategory {
  category: string;
  total: number;
  deductibleTotal: number;
}

/** GET /api/actor/money?year= — the money dashboard aggregation. `overdue` /
 * `overdueCount` are a NOW concern (never year-scoped); everything else is
 * scoped to `year`. */
export interface MoneyDashboard {
  year: number;
  thisYearGross: number;
  received: number;
  overdue: number;
  overdueCount: number;
  incomeByType: MoneyIncomeType[];
  expensesByCategory: MoneyExpenseCategory[];
  mileageKm: number;
}

export async function getMoney(tenantId: string, year?: number): Promise<MoneyDashboard> {
  const qs = year ? `?year=${year}` : '';
  return request<MoneyDashboard>(`/api/actor/money${qs}`, { tenantId });
}

/** GET /api/actor/money/export?year= — the structured year-end export the web
 * turns into a CSV download (buildMoneyCsv). Raw numbers only — no $/rate math
 * is baked in server-side. */
export interface MoneyExportIncomeRow {
  paymentId: string;
  date: string | null;
  type: string | null;
  project: string | null;
  gross: number;
  commissionPct: number | null;
  net: number;
  status: string;
  overdue: boolean;
}

export interface MoneyExportExpenseRow {
  expenseId: string;
  date: string | null;
  category: string | null;
  note: string | null;
  amount: number;
  deductible: boolean;
  mileageKm: number | null;
}

export interface MoneyExport {
  year: number;
  income: MoneyExportIncomeRow[];
  expenses: MoneyExportExpenseRow[];
  totals: {
    totalGross: number;
    totalNet: number;
    totalExpenses: number;
    totalDeductible: number;
    netIncome: number;
  };
}

export async function getMoneyExport(tenantId: string, year?: number): Promise<MoneyExport> {
  const qs = year ? `?year=${year}` : '';
  return request<MoneyExport>(`/api/actor/money/export${qs}`, { tenantId });
}

/** POST /api/actor/expenses — logs one expense (category/amount/date/note). */
export async function createExpense(
  tenantId: string,
  fields: Record<string, unknown>
): Promise<RecordRow> {
  const result = await request<{ record: RecordRow }>('/api/actor/expenses', {
    method: 'POST',
    tenantId,
    body: { fields },
  });
  return result.record;
}

export async function listExpenses(tenantId: string): Promise<ResumeEntry[]> {
  const result = await request<{ records: ResumeEntry[] }>('/api/actor/expenses', { tenantId });
  return result.records;
}

export async function deleteExpense(tenantId: string, id: string): Promise<void> {
  await request(`/api/actor/expenses/${id}`, { method: 'DELETE', tenantId });
}

/** One contract on the Documents surface. `sourceId` is present only when the
 * server later carries the linked ORIGINAL (the current documents aggregation
 * does not surface it, so the "view original" chip stays hidden until it does —
 * an honest absence, never a dead link). */
export interface DocumentContract {
  id: string;
  kind: string | null;
  status: string | null;
  rate: string | null;
  respondBy: string | null;
  keyDates: string | null;
  receivedAt: string | null;
  sourceId?: string | null;
}

export interface DocumentsTaxSummary {
  year: number;
  creditCount: number;
  deductibleTotal: number;
  expenseCount: number;
}

export interface DocumentsUnion {
  name: string | null;
  status: string | null;
  memberId: string | null;
}

/** GET /api/actor/documents?year= — Contracts + Tax-and-filings + Union. */
export interface ActorDocuments {
  contracts: DocumentContract[];
  taxSummary: DocumentsTaxSummary;
  union: DocumentsUnion;
}

export async function getDocuments(tenantId: string, year?: number): Promise<ActorDocuments> {
  const qs = year ? `?year=${year}` : '';
  return request<ActorDocuments>(`/api/actor/documents${qs}`, { tenantId });
}

export interface StatsGroupOffice {
  office: string;
  count: number;
  booked: number;
  bookingRate: number;
}

export interface StatsGroupType {
  medium: string;
  count: number;
  booked: number;
  bookingRate: number;
}

/** GET /api/actor/stats — computed analytics off real auditions. `callbackRate`
 * / `bookingRate` / `byX[].bookingRate` are FRACTIONS in [0,1]. `timeToCallback
 * Days` and `headshotAttribution` are DELIBERATELY DEFERRED (null) with a
 * `deferred` reason each — the surface renders them as honest "not enough data
 * yet", never fabricated. */
export interface ActorStats {
  total: number;
  reachedCallbackCount: number;
  bookedCount: number;
  callbackRate: number;
  bookingRate: number;
  timeToCallbackDays: number | null;
  byOffice: StatsGroupOffice[];
  byType: StatsGroupType[];
  headshotAttribution: { label?: string; note?: string } | null;
  deferred: string[];
}

export async function getStats(tenantId: string): Promise<ActorStats> {
  return request<ActorStats>('/api/actor/stats', { tenantId });
}

// ---------------------------------------------------------------------------
// B7 WEB — the tape → send-for-notes → external-reviewer-portal → received-notes
// loop, plus the Development-insights rollup. Backends: actorReviews.ts (owner
// tapes/reviews/development), actorReviewShare.ts (owner reviewer grants),
// actorReviewPortal.ts (the UNAUTHENTICATED reviewer portal — token-scoped, NO
// X-Tenant-Id). Tape + review-audio BYTES ride the object-URL pattern
// (fetchMediaFile / fetchReviewAudio / fetchPortalTape) exactly like media/
// sources above; the portal ones deliberately carry NO tenant header.
// ---------------------------------------------------------------------------

/** One tape as GET /api/actor/auditions/:id/tapes hands it back. */
export interface AuditionTape {
  id: string;
  fields: Record<string, unknown>;
  fileUrl: string;
}

export async function listAuditionTapes(tenantId: string, auditionId: string): Promise<AuditionTape[]> {
  const result = await request<{ tapes: AuditionTape[] }>(`/api/actor/auditions/${auditionId}/tapes`, { tenantId });
  return result.tapes;
}

/** POST /api/actor/media — uploads a self-tape (multipart) attached to an
 * audition (audition_id + kind=tape). Bare fetch so the browser sets the
 * multipart boundary itself (same as uploadMedia/uploadSides). */
export async function uploadTape(
  tenantId: string,
  auditionId: string,
  file: File,
  meta: { label?: string; versionLabel?: string } = {}
): Promise<{ mediaId: string }> {
  const form = new FormData();
  form.set('kind', 'tape');
  form.set('audition_id', auditionId);
  if (meta.label) form.set('label', meta.label);
  if (meta.versionLabel) form.set('version_label', meta.versionLabel);
  form.set('file', file, file.name);
  const headers = new Headers();
  if (tenantId) headers.set('X-Tenant-Id', tenantId);
  const resp = await fetch('/api/actor/media', {
    method: 'POST',
    headers,
    credentials: 'same-origin',
    body: form,
  });
  const contentType = resp.headers.get('content-type') ?? '';
  const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
  if (!resp.ok) throw extractError(resp.status, parsed);
  return parsed as { mediaId: string };
}

export type ReviewerRole = 'coach' | 'agent' | 'manager' | 'self' | 'other';

/** POST /api/actor/auditions/:id/reviewers — mints a scoped, one-time reviewer
 * link. `token`/`url` are returned ONCE here (never listed again). The caller
 * builds the shareable page URL from the token (shareablePortalUrl) — the raw
 * `url` is the bare API path the portal SPA then fetches. */
export interface CreatedReviewer {
  grantId: string;
  reviewId: string;
  token: string;
  url: string;
  expiresAt: string | null;
  tapePresent: boolean;
}

export async function createReviewer(
  tenantId: string,
  auditionId: string,
  input: { reviewerName?: string; reviewerRole?: ReviewerRole; expiresInDays?: number } = {}
): Promise<CreatedReviewer> {
  const body: Record<string, unknown> = {};
  if (input.reviewerName) body['reviewerName'] = input.reviewerName;
  if (input.reviewerRole) body['reviewerRole'] = input.reviewerRole;
  if (input.expiresInDays) body['expiresInDays'] = input.expiresInDays;
  return request<CreatedReviewer>(`/api/actor/auditions/${auditionId}/reviewers`, {
    method: 'POST',
    tenantId,
    body,
  });
}

export type ReviewerStatus = 'active' | 'submitted' | 'revoked' | 'expired';

/** One reviewer link as GET /api/actor/auditions/:id/reviewers lists it — status
 * + reviewer identity, NEVER the token (that's returned once, at creation). */
export interface ReviewerLink {
  grantId: string;
  reviewId: string;
  reviewerName: string | null;
  reviewerRole: string | null;
  status: ReviewerStatus;
  expiresAt: string | null;
  createdAt: string;
}

export async function listReviewers(tenantId: string, auditionId: string): Promise<ReviewerLink[]> {
  const result = await request<{ reviewers: ReviewerLink[] }>(`/api/actor/auditions/${auditionId}/reviewers`, {
    tenantId,
  });
  return result.reviewers;
}

export async function revokeReviewer(tenantId: string, grantId: string): Promise<void> {
  await request(`/api/actor/reviewers/${grantId}/revoke`, { method: 'POST', tenantId });
}

/** One time-marker on a review — a point in the tape (`t`, seconds) the reviewer
 * flagged, optionally with a short note. Clicking one seeks the tape. */
export interface ReviewMarker {
  t: number;
  label?: string;
}

/** One received review as GET /api/actor/auditions/:id/reviews shapes it. */
export interface AuditionReview {
  id: string;
  reviewerName: string | null;
  reviewerRole: string | null;
  body: string | null;
  transcript: string | null;
  timeMarkers: unknown;
  submittedAt: string | null;
  status: string | null;
  tapeId: string | null;
  tapeFileUrl: string | null;
  auditionId: string | null;
  audioFileUrl: string | null;
}

export async function getAuditionReviews(tenantId: string, auditionId: string): Promise<AuditionReview[]> {
  const result = await request<{ reviews: AuditionReview[] }>(`/api/actor/auditions/${auditionId}/reviews`, {
    tenantId,
  });
  return result.reviews;
}

/** Coerce the free-form `timeMarkers` a review carries into a clean marker list
 * — tolerant of the raw JSON the server stored (an array of {t,label} objects,
 * or bare numbers), never throwing on a malformed value. */
export function parseReviewMarkers(raw: unknown): ReviewMarker[] {
  if (!Array.isArray(raw)) return [];
  const out: ReviewMarker[] = [];
  for (const item of raw) {
    if (typeof item === 'number' && Number.isFinite(item)) {
      out.push({ t: item });
    } else if (item && typeof item === 'object') {
      const t = (item as { t?: unknown; time?: unknown }).t ?? (item as { time?: unknown }).time;
      if (typeof t === 'number' && Number.isFinite(t)) {
        const label = (item as { label?: unknown; note?: unknown }).label ?? (item as { note?: unknown }).note;
        out.push({ t, label: typeof label === 'string' && label ? label : undefined });
      }
    }
  }
  return out;
}

/** The review's recorded audio as an in-memory object URL + mime (tenant-auth,
 * X-Tenant-Id header — an <audio> src can't carry it). Callers MUST
 * URL.revokeObjectURL when done. Throws ApiError on any non-2xx. */
export async function fetchReviewAudio(tenantId: string, reviewId: string): Promise<MediaFile> {
  const headers = new Headers();
  if (tenantId) headers.set('X-Tenant-Id', tenantId);
  const resp = await fetch(`/api/actor/reviews/${reviewId}/audio`, { headers, credentials: 'same-origin' });
  if (!resp.ok) {
    const contentType = resp.headers.get('content-type') ?? '';
    const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
    throw extractError(resp.status, parsed);
  }
  const mimeType = (resp.headers.get('content-type') ?? 'application/octet-stream').split(';')[0]!.trim();
  const blob = await resp.blob();
  return { objectUrl: URL.createObjectURL(blob), mimeType };
}

// ---- The Development-insights rollup (GET /api/actor/development). ----------

export interface DevelopmentFeedback {
  reviewId: string;
  reviewer: string | null;
  reviewerRole: string | null;
  snippet: string;
  tapeId: string | null;
  tapeFileUrl: string | null;
  auditionId: string | null;
  submittedAt: string | null;
}

export interface DevelopmentTheme {
  phrase: string;
  count: number;
}

export interface DevelopmentProgress {
  total: number;
  reachedCallbackCount: number;
  bookedCount: number;
  callbackRate: number;
  bookingRate: number;
}

export interface DevelopmentClass {
  id: string;
  fields: Record<string, unknown>;
}

export interface Development {
  recentFeedback: DevelopmentFeedback[];
  recurringThemes: DevelopmentTheme[];
  callbackProgress: DevelopmentProgress;
  classes: DevelopmentClass[];
  notes?: string[];
}

export async function getDevelopment(tenantId: string): Promise<Development> {
  return request<Development>('/api/actor/development', { tenantId });
}

// ---------------------------------------------------------------------------
// THE UNAUTHENTICATED REVIEWER PORTAL (actorReviewPortal.ts). Every call here is
// authorized SOLELY by the grant token in the path — there is NO session, NO
// X-Tenant-Id, NO tenant scope for an external party (D9: "never accounts"). A
// bad/expired/revoked/used token is an EXPECTED outcome (404 / 410), surfaced
// calmly, never a crash. These deliberately do NOT go through `request` (which
// is the tenant-scoped client) — they're plain, header-free fetches.
// ---------------------------------------------------------------------------

/** The reviewer-facing shareable page URL. The bare `/portal/review/:token`
 * path is a registered SERVER route (returns JSON to fetch), so the human-
 * facing link is the HASH route that loads THIS SPA, which then fetches the
 * JSON itself. Absolute (origin-qualified) so it survives a copy-paste into an
 * email. */
export function shareablePortalUrl(token: string): string {
  const origin = typeof window !== 'undefined' && window.location ? window.location.origin : '';
  return `${origin}/#/portal/review/${token}`;
}

/** The reviewer's minimal scoped context. A null return means the token is
 * invalid/expired/revoked/target-gone (a uniform 404 server-side) — the caller
 * renders the graceful "this link is no longer active" state. */
export interface ReviewPortalContext {
  tapePresent: boolean;
  audition: { role: string | null; project: string | null } | null;
  reviewerName: string | null;
  reviewerRole: string | null;
  alreadySubmitted: boolean;
  tapeUrl: string | null;
}

export async function getReviewPortalContext(token: string): Promise<ReviewPortalContext | null> {
  const resp = await fetch(`/portal/review/${token}`, { credentials: 'same-origin' });
  if (resp.status === 404) return null;
  const contentType = resp.headers.get('content-type') ?? '';
  const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
  if (!resp.ok) throw extractError(resp.status, parsed);
  return parsed as ReviewPortalContext;
}

/** The granted tape's bytes as an object URL + mime — TOKEN-scoped, header-free
 * (must NOT send X-Tenant-Id: the reviewer has no tenant). Callers MUST
 * URL.revokeObjectURL when done. Throws ApiError on any non-2xx. */
export async function fetchPortalTape(token: string): Promise<MediaFile> {
  const resp = await fetch(`/portal/review/${token}/tape`, { credentials: 'same-origin' });
  if (!resp.ok) {
    const contentType = resp.headers.get('content-type') ?? '';
    const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
    throw extractError(resp.status, parsed);
  }
  const mimeType = (resp.headers.get('content-type') ?? 'application/octet-stream').split(';')[0]!.trim();
  const blob = await resp.blob();
  return { objectUrl: URL.createObjectURL(blob), mimeType };
}

/** The outcome of a portal submit. `submitted` — done (act-once). `consumed` —
 * this review was already submitted (410). `invalid` — the link is no longer
 * active (404). Never throws for these EXPECTED outcomes; a caller renders each
 * calmly. */
export type PortalSubmitOutcome =
  | { outcome: 'submitted'; transcriptFilled: boolean }
  | { outcome: 'consumed' }
  | { outcome: 'invalid' };

/** POST /portal/review/:token — submit the review (act-once). Sends multipart
 * when an audio recording is present (so the browser sets the boundary), else a
 * plain JSON body. `timeMarkers` is serialised to a JSON string either way. */
export async function submitReviewPortal(
  token: string,
  input: { body?: string; audio?: Blob; audioFilename?: string; timeMarkers?: ReviewMarker[] }
): Promise<PortalSubmitOutcome> {
  const markersJson =
    input.timeMarkers && input.timeMarkers.length > 0 ? JSON.stringify(input.timeMarkers) : undefined;

  let resp: Response;
  if (input.audio) {
    const form = new FormData();
    if (input.body) form.set('body', input.body);
    if (markersJson) form.set('time_markers', markersJson);
    form.set('file', input.audio, input.audioFilename ?? 'review-audio.webm');
    resp = await fetch(`/portal/review/${token}`, { method: 'POST', credentials: 'same-origin', body: form });
  } else {
    const body: Record<string, unknown> = {};
    if (input.body) body['body'] = input.body;
    if (markersJson) body['time_markers'] = markersJson;
    resp = await fetch(`/portal/review/${token}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
  }

  if (resp.status === 410) return { outcome: 'consumed' };
  if (resp.status === 404) return { outcome: 'invalid' };
  const contentType = resp.headers.get('content-type') ?? '';
  const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
  if (!resp.ok) throw extractError(resp.status, parsed);
  return { outcome: 'submitted', transcriptFilled: Boolean((parsed as { transcriptFilled?: boolean })?.transcriptFilled) };
}
